"use client";
import { CaseStudySlide } from "@/commonComponents/cards/case-study-slide";
import {
  MotionStack,
  MotionVariantProps,
} from "@/commonComponents/motion-components";
import SectionWrapper from "@/commonComponents/section-wrapper";
import { Grid, Stack } from "@mui/material";
import { StaticImageData } from "next/image";
import { textEllipsis } from "@util/string-helpers";
import AnimatedButton, { AnimatedButtonProps } from "@cc/animated-button";

export type NewsAndMediaBlogsSectionProps = {
  title: string;
  ButtonProps?: AnimatedButtonProps;
  mostReadData: {
    image: StaticImageData | string;
    href: string;
    tag: string;
    title: string;
    subTitle: string;
  };
  gridData: {
    image: StaticImageData | string;
    href: string;
    tag: string;
    title: string;
    subTitle: string;
  }[];
  navId?: string;
};

const NewsAndMediaBlogsSection = (props: NewsAndMediaBlogsSectionProps) => {
  const { title, ButtonProps, mostReadData, gridData, navId } = props;
  return (
    <SectionWrapper
      SectionHeaderProps={{
        title: title,
      }}
      SectionProps={{
        sx: {
          background: (theme) => theme.palette.gradient.darkToLight,
        },
      }}
      navId={navId || "Blogs"}
    >
      <Grid container columnSpacing={3} rowSpacing={5}>
        <Grid item xs={12} md={6}>
          <CaseStudySlide
            img={mostReadData.image}
            href={mostReadData.href}
            tag={mostReadData.tag}
            title={mostReadData.title}
            subTitle={mostReadData.subTitle}
            TagTypographyProps={{
              bgcolor: "none",
              p: 0,
              color: "common.white",
              mt: 1,
              variant: "body2",
              variants: bottomToToptextLowOpacity,
            }}
            CardWrapperProps={{
              rowGap: 1,
            }}
            ContentWrapperProps={{
              rowGap: { xs: 1.5, md: 1 },
              mt: { xs: 0.5, md: 0 },
            }}
            ImageProps={{
              width: 1,
              maxWidth: 644,
              height: { xs: 227, md: 520 },
            }}
            hoverContent="Read More"
            mostRead
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MotionStack rowGap={{ xs: 5, md: 3 }}>
            {gridData.map((item, idx) => (
              <CaseStudySlide
                key={idx}
                img={item.image}
                href={item.href}
                tag={item.tag}
                title={item.title}
                subTitle={item.subTitle}
                CardWrapperProps={{
                  direction: "row",
                  alignItems: "stretch",
                  columnGap: 2.5,
                }}
                TagTypographyProps={{
                  bgcolor: "none",
                  p: 0,
                  color: "common.white",
                  mt: 1,
                  variant: "body2",
                  sx: {
                    opacity: { xs: 0.8, md: 1 },
                  },
                }}
                TitleTypographyProps={{
                  sx: textEllipsis(3),
                }}
                SubTitleTypographyProps={{
                  sx: {
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "normal",
                    width: 1,
                  },
                }}
                ImageProps={{
                  width: { xs: 120, md: 205 },
                  height: "auto",
                  minHeight: { xs: 140, md: 205 },
                }}
                ContentWrapperProps={{
                  rowGap: 1,
                  my: "auto",
                }}
                hoverContent="Read More"
                mergeTagToContent
                hideSubtitleOnMobile
              />
            ))}
          </MotionStack>
        </Grid>
      </Grid>
      {ButtonProps && ButtonProps.href && ButtonProps.children && (
        <Stack pt={{ xs: 4, md: 7 }} alignItems={"center"}>
          <AnimatedButton color={"secondary"} {...ButtonProps} />
        </Stack>
      )}
    </SectionWrapper>
  );
};

export default NewsAndMediaBlogsSection;

export const bottomToToptextLowOpacity: MotionVariantProps = {
  initial: {
    y: "50%",
    opacity: 0,
  },
  animate: {
    opacity: 0.8,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
};
